import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@components/ui'
import {
  Settings as SettingsIcon,
  Clock,
  RotateCcw,
  FileCheck,
  Shield,
  Fingerprint,
  Building2,
  Cog,
  BookOpen,
  FileText,
  Package,
  Map,
  HelpCircle,
  GitBranch,
  ShieldAlert,
  Ticket,
  Users,
} from 'lucide-react'
import { useAuthStore } from '@store/authStore'
import type { UserPermissions } from '@store/authStore'
import { useSettingsStore } from './store'
import { clsx } from 'clsx'
import { KycHoursTab } from './components/KycHoursTab'
import { AmlHoursTab } from './components/AmlHoursTab'
import { BiometricsHoursTab } from './components/BiometricsHoursTab'
import { KybHoursTab } from './components/KybHoursTab'
import { GeneralHoursTab } from './components/GeneralHoursTab'
import { WorkflowsHoursTab } from './components/WorkflowsHoursTab'
import { RiskFraudHoursTab } from './components/RiskFraudHoursTab'
import { FormulasTab } from './components/FormulasTab'
import { AuditLogTab } from './components/AuditLogTab'
import { ProductsTab } from './components/ProductsTab'
import { RoadmapKanban } from '@features/feedback/components/RoadmapKanban'
import { HowItWorksTab } from './components/HowItWorksTab'
import { JiraPromptsTab } from './components/JiraPromptsTab'
import { TeamMembersTab } from './components/TeamMembersTab'
import type { LucideIcon } from 'lucide-react'

// Main settings sections with permission requirements
type MainSection = 'hours' | 'formulas' | 'audit' | 'products' | 'team' | 'roadmap' | 'jira-prompts' | 'how-it-works'

interface SectionDef {
  id: MainSection
  label: string
  icon: LucideIcon
  // Permission key required to see this tab, or null if visible to all authenticated users
  requiredPermission: keyof UserPermissions | null
}

const allSections: SectionDef[] = [
  // Visible to every authenticated user (view-only); editing is gated separately by
  // canEditScopeHours (read-only <fieldset> + hidden Reset for non-editors).
  { id: 'hours', label: 'Scope Hours', icon: Clock, requiredPermission: null },
  { id: 'formulas', label: 'Formulas', icon: BookOpen, requiredPermission: 'canEditFormulas' },
  { id: 'audit', label: 'Audit Log', icon: FileText, requiredPermission: 'canViewAuditLog' },
  { id: 'products', label: 'Products', icon: Package, requiredPermission: 'canEditProducts' },
  { id: 'team', label: 'Team Members', icon: Users, requiredPermission: 'canEditProducts' },
  { id: 'roadmap', label: 'Roadmap', icon: Map, requiredPermission: 'canEditRoadmap' },
  { id: 'jira-prompts', label: 'Jira Prompts', icon: Ticket, requiredPermission: 'canEditJiraPrompts' },
  { id: 'how-it-works', label: 'How It Works', icon: HelpCircle, requiredPermission: null },
]

// Hour sub-tabs
const hourTabs = [
  { id: 'general', label: 'General', icon: Cog },
  { id: 'kyc', label: 'KYC', icon: FileCheck },
  { id: 'aml', label: 'AML', icon: Shield },
  { id: 'biometrics', label: 'Biometrics/IDV', icon: Fingerprint },
  { id: 'kyb', label: 'KYB', icon: Building2 },
  { id: 'workflows', label: 'Workflows', icon: GitBranch },
  { id: 'risk_fraud', label: 'Risk & Fraud', icon: ShieldAlert },
]

export function Settings() {
  const { user } = useAuthStore()
  const { resetToDefaults } = useSettingsStore()
  const { tab } = useParams<{ tab?: string }>()
  const navigate = useNavigate()
  const [activeHourTab, setActiveHourTab] = useState('general')

  // Scope Hours is visible to every authenticated user (read-only); only editors
  // (canEditScopeHours) may change the values.
  const canEditHours = user?.permissions?.canEditScopeHours ?? false

  // Filter sections based on user permissions
  const visibleSections = useMemo(() => {
    if (!user) return []
    return allSections.filter((section) => {
      // Tabs with no permission requirement are visible to all authenticated users
      if (section.requiredPermission === null) return true
      // Otherwise, check the specific permission
      return user.permissions?.[section.requiredPermission] === true
    })
  }, [user])

  const visibleTabIds = visibleSections.map(s => s.id)

  // Derive active section from URL param, default to first visible tab
  const defaultTab = visibleTabIds[0] || 'how-it-works'
  const activeSection: MainSection = visibleTabIds.includes(tab as MainSection)
    ? (tab as MainSection)
    : defaultTab

  // Redirect to URL with tab if none specified or invalid
  useEffect(() => {
    if (!tab || !visibleTabIds.includes(tab as MainSection)) {
      navigate(`/settings/${activeSection}`, { replace: true })
    }
  }, [tab, activeSection, navigate, visibleTabIds])

  const setActiveSection = (section: MainSection) => {
    navigate(`/settings/${section}`)
  }

  // If user has no visible sections at all (shouldn't happen since how-it-works is always visible)
  if (visibleSections.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <SettingsIcon className="text-primary" size={24} />
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Settings</h1>
        </div>

        <Card variant="bordered">
          <CardContent>
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🔒</div>
              <h2 className="text-lg font-medium text-[var(--text-primary)] mb-2">Access Restricted</h2>
              <p className="text-[var(--text-secondary)]">
                The Settings area is only accessible to authorized administrators.
              </p>
              <p className="text-[var(--text-muted)] text-sm mt-2">
                If you believe you should have access, please contact your administrator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const renderHourTabContent = () => {
    switch (activeHourTab) {
      case 'general':
        return <GeneralHoursTab />
      case 'kyc':
        return <KycHoursTab />
      case 'aml':
        return <AmlHoursTab />
      case 'biometrics':
        return <BiometricsHoursTab />
      case 'kyb':
        return <KybHoursTab />
      case 'workflows':
        return <WorkflowsHoursTab />
      case 'risk_fraud':
        return <RiskFraudHoursTab />
      default:
        return null
    }
  }

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'hours':
        return (
          <Card variant="bordered">
            <div className="p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="text-primary" size={20} />
                  <div>
                    <h2 className="text-lg font-medium text-[var(--text-primary)] flex items-center gap-2">
                      Scope Hour Configuration
                      {!canEditHours && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] border border-[var(--border-color)]">
                          View only
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-[var(--text-muted)]">
                      {canEditHours
                        ? 'Configure default hours for each feature in the Scope module'
                        : 'View the default hours for each feature in the Scope module (read-only)'}
                    </p>
                  </div>
                </div>
                {canEditHours && (
                  <button
                    onClick={() => {
                      if (confirm('Reset all hour settings to defaults?')) {
                        resetToDefaults()
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] rounded-lg transition-colors"
                  >
                    <RotateCcw size={14} />
                    Reset to Defaults
                  </button>
                )}
              </div>
            </div>

            <div className="flex">
              {/* Tab Navigation */}
              <div className="w-48 border-r border-[var(--border-color)] p-2">
                {hourTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveHourTab(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors mb-1',
                      activeHourTab === tab.id
                        ? 'bg-primary/10 text-primary'
                        : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
                    )}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content — read-only for viewers. <fieldset disabled> disables every
                  input/select/button inside in one go (browser-native), so viewers can switch
                  sub-tabs and read values but can't change them (the API also 403s their writes). */}
              <fieldset disabled={!canEditHours} className="flex-1 p-4 max-h-[calc(100vh-320px)] overflow-y-auto border-0 m-0 min-w-0">
                {renderHourTabContent()}
              </fieldset>
            </div>
          </Card>
        )

      case 'formulas':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <FormulasTab />
            </div>
          </Card>
        )

      case 'audit':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <AuditLogTab />
            </div>
          </Card>
        )

      case 'products':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <ProductsTab />
            </div>
          </Card>
        )

      case 'team':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <TeamMembersTab />
            </div>
          </Card>
        )

      case 'roadmap':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <RoadmapKanban />
            </div>
          </Card>
        )

      case 'jira-prompts':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <JiraPromptsTab />
            </div>
          </Card>
        )

      case 'how-it-works':
        return (
          <Card variant="bordered">
            <div className="p-4">
              <HowItWorksTab />
            </div>
          </Card>
        )

      default:
        return null
    }
  }

  // Determine the page title based on whether user has any admin tabs
  const hasAdminTabs = visibleSections.some(s => s.requiredPermission !== null)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SettingsIcon className="text-primary" size={24} />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">
              {hasAdminTabs ? 'Admin Settings' : 'Settings'}
            </h1>
            <p className="text-sm text-[var(--text-muted)]">
              {hasAdminTabs
                ? 'Formula configuration, audit logs, and product management'
                : 'Learn how DealHub works'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Main Section Tabs — only show tabs the user has permission for */}
      <div className="flex items-center gap-2 border-b border-[var(--border-color)] pb-2 overflow-x-auto">
        {visibleSections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
              activeSection === section.id
                ? 'bg-primary/10 text-primary border border-primary/30'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)]'
            )}
          >
            <section.icon size={16} />
            {section.label}
          </button>
        ))}
      </div>

      {/* Section Content */}
      {renderSectionContent()}
    </div>
  )
}
